import type { ReactNode } from 'react';
import { Box, Button, ButtonRow, Heading, List } from '@ksyos/design-system';
import { v4 as uuidv4 } from 'uuid';
import type { IAsset } from '../../../../../../common/validation/assets';
import type { IAltoStepProps } from '../../NewAltoDermaOthProcedure';
import type { IRegularStepProps } from '../../NewDermaOthProcedure';
import { CustomContainer } from '../../../../elements/molecules/customContainer/CustomContainer';
import { AssetThumbnail } from '../../../../elements/organisms/assetDropzone/AssetThumbnail';
import { PageIntro } from '../../../../elements/organisms/pageIntro/PageIntro';

const maxFiles = 4;
export interface IResultProps {
    answer: string | null;
    children?: ReactNode;
    question: string;
}

function Result({ answer, children, question }: IResultProps) {
    return (
        <List.Item>
            <Heading
                mb={2}
                size="small"
            >
                {question}
            </Heading>
            <List>
                <List.Item>{answer}</List.Item>
            </List>
            {children}
        </List.Item>
    );
}

export function Step5(
    props: (IRegularStepProps | IAltoStepProps) & { altoFlow?: boolean; csPatientName?: string | null }
) {
    function createPlaceholderImages(currentFiles: IAsset[]) {
        const numberOfPlaceholders = maxFiles - currentFiles.length;
        for (let i = 0; i < numberOfPlaceholders; i++) {
            currentFiles.push({
                filename: '',
                s3Id: uuidv4(),
                thumbnailDataUrl: '',
            });
        }
    }

    const files = props.procedure.customControlData.overviewFiles.concat(props.procedure.customControlData.detailFiles);
    if (files.length < maxFiles) {
        createPlaceholderImages(files);
    }

    return (
        <>
            <PageIntro
                subtitle="Controleer als laatste of je alle vragen hebt beantwoord."
                title="Controle"
            />
            <CustomContainer>
                <Heading
                    mb={6}
                    size="medium"
                >
                    Foto&apos;s
                </Heading>
                <Box
                    display="flex"
                    flexWrap="wrap"
                    justifyContent="center"
                >
                    {files.map((file) => (
                        <AssetThumbnail
                            file={file}
                            key={file.s3Id}
                            onlyThumbnails
                            showFilenames
                            smallThumbnails
                        />
                    ))}
                </Box>
                <Heading
                    mb={6}
                    size="medium"
                >
                    Vragen
                </Heading>
                <List
                    counterColor="secondary"
                    counterFontWeight="medium"
                    styleType="counter"
                >
                    <List.Item>
                        <Heading
                            mb={2}
                            size="small"
                        >
                            Hoe voelt de aandoening?
                        </Heading>
                        <List>
                            {props.procedure.customControlData.condition.jeukt && <List.Item>Het jeukt</List.Item>}
                            {props.procedure.customControlData.condition.pijn && <List.Item>Het doet pijn</List.Item>}
                            {props.procedure.customControlData.condition.anders && (
                                <List.Item>{props.procedure.customControlData.condition.anders}</List.Item>
                            )}
                        </List>
                    </List.Item>
                    <Result
                        answer={props.procedure.customControlData.conditionStarted}
                        question="Wanneer is het begonnen?"
                    />
                    <Result
                        answer={props.procedure.customControlData.conditionChanged}
                        question="Is het ondertussen veranderd?"
                    />
                    <Result
                        answer={props.procedure.customControlData.conditionColorChanged}
                        question="Is de kleur veranderd?"
                    />
                    <Result
                        answer={props.procedure.customControlData.conditionSizeChanged}
                        question="Is de grootte veranderd?"
                    />
                    <Result
                        answer={props.procedure.customControlData.conditionBefore}
                        question={`${
                            props.altoFlow ? `Heeft ${props.csPatientName}` : 'Heb je'
                        } je deze aandoening eerder gehad?`}
                    >
                        {props.procedure.customControlData.conditionBefore === 'Ja' && (
                            <Result
                                answer={props.procedure.customControlData.conditionBeforeRemark}
                                question="Wanneer en wat is er toen aan gedaan?"
                            />
                        )}
                    </Result>
                    <Result
                        answer={props.procedure.customControlData.treatingCondition}
                        question={
                            props.altoFlow ? 'Is er al een behandeling geprobeerd?' : 'Behandel je de aandoening zelf?'
                        }
                    >
                        {props.procedure.customControlData.treatingCondition === 'Ja' && (
                            <Result
                                answer={props.procedure.customControlData.treatingConditionRemark}
                                question="Hoe behandel je de aandoenig?"
                            />
                        )}
                    </Result>
                    <Result
                        answer={props.procedure.customControlData.extraQuestion}
                        question="Heb je een vraag of opmerking aan de arts?"
                    />
                </List>
                <ButtonRow justify="right">
                    {props.submit && (
                        <Button
                            color="secondary"
                            isDisabled={props.isSubmitting}
                            onClick={props.submit}
                            variant="solid"
                            width="full-below-small"
                        >
                            {!props.isSubmitting ? 'Verzenden' : 'Bezig met verwerken...'}
                        </Button>
                    )}
                </ButtonRow>
            </CustomContainer>
        </>
    );
}
